import React from 'react';
import styled from 'styled-components';
import qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';

import { Button, useScreenQuery } from 'playup-web-core';

import PortalSessionContext from '../../contexts/PortalSessionContext/PortalSessionContext';
import AppSelector from './AppSelector/AppSelector';
import AppMenu from './AppMenu/AppMenu';
import BalanceMenu from './BalanceMenu/BalanceMenu';
import GlobalContext from '../../contexts/GlobalContext';
import appConfig from '../../appConfig';
import { BonusContextProvider } from '../../contexts/BonusContext';

const availableApps = appConfig.clientApps;

const Navbar = () => {
	const history = useHistory();
	const { isSmall: isSmallScreen } = useScreenQuery();
	const { user, logout, clientUserBonuses, refreshClientUserBonuses } = React.useContext(PortalSessionContext);
	const { hideBalance, setHideBalance } = React.useContext(GlobalContext);
	const isLoggedIn = !!user?.id;
	const isVerified = !!user?.id_verified;

	const location = useLocation();

	const activeApp = React.useMemo(() => {
		const activeLocationApp = availableApps.find((app) => location.pathname.startsWith(app.link));

		return activeLocationApp || availableApps[0];
	}, [location.pathname]);

	const navRef = React.useRef<HTMLElement>(null);

	const openLogin = () => {
		history.push({
			search: qs.stringify({ modal: 'login' }, { addQueryPrefix: true }),
		});
	};

	const openSignup = () => {
		history.push({
			search: qs.stringify({ modal: 'signup' }, { addQueryPrefix: true }),
		});
	};

	React.useEffect(() => {
		document.body.style.paddingTop = `${navRef.current?.offsetHeight}px`;
	}, [isSmallScreen]);

	const onUpdatebonuses = React.useCallback(() => {
		refreshClientUserBonuses(activeApp.clientId);
	}, [refreshClientUserBonuses, activeApp.clientId]);

	return (
		<Wrapper ref={navRef}>
			<NavContent isSmallScreen={isSmallScreen}>
				<NavLeft isSmallScreen={isSmallScreen}>
					<AppSelector activeApp={activeApp} isSmallScreen={isSmallScreen} />
				</NavLeft>
				<NavRight>
					{!isLoggedIn && (
						<>
							<StyledButton
								kind="transparent-white"
								isSmallScreen={isSmallScreen}
								contentSize={isSmallScreen ? 14 : 18}
								onClick={openLogin}
							>
								Login
							</StyledButton>
							<StyledButton isSmallScreen={isSmallScreen} contentSize={isSmallScreen ? 14 : 18} onClick={openSignup}>
								Join
							</StyledButton>
						</>
					)}
					{isLoggedIn && (
						<BonusContextProvider>
							<BalanceMenu
								onUpdatebonuses={onUpdatebonuses}
								hideBalance={hideBalance}
								setHideBalance={setHideBalance}
								user={user!}
								clientUserBonuses={clientUserBonuses}
								activeApp={activeApp}
								isSmallScreen={isSmallScreen}
								logout={logout}
								sessionStartTime={user!.last_login_at}
							/>
						</BonusContextProvider>
					)}
					<AppMenu
						logout={logout}
						isSmallScreen={isSmallScreen}
						isLoggedIn={isLoggedIn}
						isVerified={isVerified}
						hideBalance={hideBalance}
						setHideBalance={setHideBalance}
					/>
				</NavRight>
			</NavContent>
		</Wrapper>
	);
};

export default Navbar;

const Wrapper = styled.nav`
	position: fixed;
	left: 0;
	right: 0;
	top: 0;
	width: 100%;
	z-index: 50;
	background-color: ${({ theme }) => theme.colors.darkR};
`;

const NavContent = styled.div<{ isSmallScreen: boolean }>`
	display: flex;
	justify-content: space-between;
	max-width: 1300px;
	height: ${({ isSmallScreen }) => (isSmallScreen ? '50px' : '80px')};
	margin: auto;
	padding: 0.25rem 0.5rem;
`;

const NavLeft = styled.div<{ isSmallScreen: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 50%;
	max-width: 50%;
`;

const NavRight = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const StyledButton = styled(Button)<{ isSmallScreen: boolean }>`
	margin: 5px;
	padding: ${({ isSmallScreen }) => (isSmallScreen ? '0 5px;' : '0 10px;')};
	width: initial;
	min-width: ${({ isSmallScreen }) => (isSmallScreen ? '67px' : '90px')};
	height: ${({ isSmallScreen }) => (isSmallScreen ? '27px' : '36px')};

	&:focus {
		outline: none;
	}
`;
